import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type } from "arktype";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { OverlayShell } from "@/components/ui/overlay";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { format } from "@/lib/format";

const cashCountForm = type({
	"counted_cents?": "number.integer >= 0",
	counted_on: "string",
}).narrow(
	(value, ctx) =>
		value.counted_cents !== undefined ||
		ctx.reject({
			expected: "counted_cents",
			actual: "missing counted_cents",
		}),
);

export function CashCountModal({
	date,
	balanceCents,
	onClose,
}: {
	date: string;
	balanceCents: number;
	onClose: () => void;
}) {
	const queryClient = useQueryClient();
	const { mutate, isPending } = useMutation(
		orpc.financeiro.cash.count.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });
				toast.success("Contagem registrada");
				onClose();
			},
			onError: () => toast.error("Não foi possível registrar a contagem"),
		}),
	);
	const {
		control,
		handleSubmit,
		trigger,
		formState: { isValid },
	} = useForm<typeof cashCountForm.infer>({
		resolver: arktypeResolver(cashCountForm),
		mode: "onChange",
		defaultValues: {
			counted_on: date,
		},
	});
	const close = () => {
		if (!isPending) {
			onClose();
		}
	};

	useEffect(() => {
		void trigger();
	}, [trigger]);

	return (
		<OverlayShell label="Contar caixa" onClose={close} className="items-center justify-center p-4">
			<form
				aria-busy={isPending}
				onSubmit={handleSubmit((value) => {
					if (value.counted_cents === undefined) {
						return;
					}

					mutate({
						counted_cents: value.counted_cents,
						counted_on: value.counted_on,
					});
				})}
				className="overlay-panel max-w-md gap-4 p-5"
			>
				<div>
					<h2 className="type-section-title text-foreground">Contar caixa</h2>
					<p className="mt-1 text-xs text-muted-foreground">
						Saldo esperado em {format.date(date)}: {FinanceiroLabels.money(balanceCents)}
					</p>
				</div>

				<div className="grid grid-cols-2 gap-3">
					<label className="flex flex-col gap-1.5 text-sm font-medium">
						Valor contado
						<Controller
							control={control}
							name="counted_cents"
							render={({ field }) => (
								<Input
									type="text"
									inputMode="numeric"
									value={field.value === undefined ? "" : FinanceiroLabels.money(field.value)}
									disabled={isPending}
									onChange={(event) => {
										const digits = event.currentTarget.value.replaceAll(/\D/gu, "");

										field.onChange(digits.length === 0 ? undefined : Number(digits));
									}}
									data-slot="cash-count-amount"
									className="font-mono tabular-nums"
								/>
							)}
						/>
					</label>
					<label className="flex flex-col gap-1.5 text-sm font-medium">
						Data
						<Controller
							control={control}
							name="counted_on"
							render={({ field }) => (
								<DatePicker
									value={field.value}
									onChange={(value) => value !== null && field.onChange(value)}
									disabled
									containerClassName="w-full"
									className="w-full justify-between"
								/>
							)}
						/>
					</label>
				</div>

				<div className="flex justify-end gap-2">
					<Button type="button" variant="ghost" disabled={isPending} onClick={close}>
						Cancelar
					</Button>
					<Button type="submit" disabled={!isValid || isPending}>
						{isPending && <Loader2 className="animate-spin" />}
						{isPending && "Registrando"}
						{!isPending && "Registrar contagem"}
					</Button>
				</div>
			</form>
		</OverlayShell>
	);
}
